// @flow
import * as React from 'react';
import { sprintf } from 'sprintf-js';
import invariant from 'invariant';
import styled from 'styled-components';
import { isFunction, deepFlatten, isDefined } from 'utils/functional';
import * as localStorage from 'utils/localStorage';
import scrollIntoView from 'utils/scrollIntoView';
import { ExpandButton, LinkButton } from 'components/Button';
import Tile, { TileHeader, TileBody } from 'components/Tile';
import { H4 } from 'components/Text';
import { GeneralError } from 'components/Form';
import Divider from 'components/Divider';
import Box from 'components/Box';
import ToggleHelperContainer from 'containers/ToggleHelperContainer';
import FormContext from './FormContext';
import { colors, fontWeights, gutterWidth } from '../../theme';

const { useState, useEffect, useContext } = React;

type Row = {
  toggle?: string,
  fields: Array<string>,
  noColumns?: boolean,
  columns?: number,
  toggleQaId?: string
};

type Subsection = {
  title?: string,
  rows: Array<?Row>,
  showGeneralErrors?: boolean,
  matchError?: string
};

type Section = {
  id: string,
  title?: string,
  subsections: Array<Subsection>
};

export type Sections = Array<Section>;

const ToggleLink = styled.div`
  padding-bottom: 31px;
  &::before {
    content: '+ ';
    color: ${colors.waterBlue};
    font-weight: ${fontWeights.semiBold};
  }
`;

const ToggleSection = styled(
  ({
    children,
    title,
    className,
    toggleQaId
  }: {
    children: React.Node,
    title?: string,
    className: string,
    toggleQaId?: string
  }) =>
    isDefined(title) ? (
      <ToggleHelperContainer>
        {(toggled, toggle) => (
          <Box position="relative" pt={31} className={className}>
            <Box>
              {toggled ? (
                children
              ) : (
                <ToggleLink>
                  <LinkButton onClick={toggle} data-qa-id={toggleQaId}>
                    {title}
                  </LinkButton>
                </ToggleLink>
              )}
            </Box>
          </Box>
        )}
      </ToggleHelperContainer>
    ) : (
      children
    )
)`
  &:last-child {
    &::after {
      opacity: 0;
    }
  }
`;

const Field = ({ field, fields }) => {
  const f = fields[field];
  invariant(f, `Field with name '${field}' does not exist`);
  return isFunction(f) ? f() : f;
};

const SectionFields = ({
  row,
  fields
}: {
  row: Row,
  fields: { [string]: React.Node | (() => React.Node) }
}) => (
  <Box display="flex" flexWrap="wrap" mx="-8px" width="calc(100% + 16px)">
    {row.fields.map((fieldName, fieldIndex, rowFields) => {
      const columns =
        row && row.noColumns
          ? 1
          : row && isDefined(row.columns)
          ? row.columns
          : rowFields.length;
      return (
        <Box
          key={fieldName}
          width={[1, null, null, 1 / columns]}
          px="8px"
          flex="0 0 auto"
        >
          <Field fields={fields} field={fieldName} />
        </Box>
      );
    })}
  </Box>
);

const testMode = localStorage.getItem('testMode');

const MultiSection = ({
  sections,
  fields,
  initialSection
}: {
  sections: Sections,
  fields: { [string]: React.Node | (() => React.Node) },
  initialSection?: number
}) => {
  const { errors } = useContext(FormContext) || {};

  const [currentSection, setCurrentSection] = useState(initialSection || 0);
  const [[errorMap, mostRecentErrors], setErrorMap] = useState([{}, errors]);

  useEffect(
    () => {
      setCurrentSection(initialSection || 0);
    },
    [initialSection]
  );

  useEffect(
    () => {
      if (errors !== mostRecentErrors) {
        const errorsBySection = sections.reduce((obj, section) => {
          const sectionFields = deepFlatten(
            section.subsections.map(subsection =>
              subsection.rows.filter(r => r).map(row => (row ? row.fields : []))
            )
          );
          return {
            ...obj,
            [section.id]: sectionFields.some(field => errors[field])
          };
        }, {});
        setErrorMap([errorsBySection, errors]);
      }
    },
    [errors, mostRecentErrors, sections]
  );

  const sectionHasError = (sectionId: string) => errorMap[sectionId];

  const toggleSection = (event, sectionIndex, scrollPadding) => {
    setCurrentSection(currentSection === sectionIndex ? -1 : sectionIndex);

    if (currentSection !== sectionIndex) {
      event.persist();

      setTimeout(() => {
        scrollIntoView(event.target, { padding: scrollPadding });
      }, 100);
    }
  };

  return (
    <Box>
      <Box position="relative" width={1} minHeight="100%">
        {sections.map((section, sectionIndex) => {
          const isActive = currentSection === sectionIndex || testMode;
          const onClickToggleSection = (event, scrollPadding) =>
            toggleSection(event, sectionIndex, scrollPadding);
          const hasError = sectionHasError(section.id);
          return (
            <Box
              key={section.id}
              my={gutterWidth / 2}
              mt={sectionIndex === 0 && 0}
            >
              <Tile>
                <TileHeader
                  isActive={isActive}
                  hasError={hasError}
                  onClick={event => onClickToggleSection(event, 32)}
                >
                  <Box
                    width="100%"
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    position="relative"
                  >
                    <H4
                      m="0"
                      whiteSpace="nowrap"
                      textOverflow="ellipsis"
                      overflow="hidden"
                      pr={12}
                    >
                      {section.title}
                    </H4>

                    <Box mr={[-12, 0]}>
                      {hasError ? (
                        <Box
                          borderRadius="50%"
                          width={40}
                          height={40}
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          color={colors.scarlet}
                          bg="white"
                        >
                          <H4 tag="div">!</H4>
                        </Box>
                      ) : (
                        <ExpandButton
                          display="block"
                          data-qa-id="toggle-basic-info"
                          isActive={isActive}
                          onClick={event => onClickToggleSection(event, 64)}
                          title={sprintf(getText('%(toggle)s %(title)s'), {
                            toggle: isActive ? 'Collapse' : 'Expand',
                            title: section.title
                          })}
                        />
                      )}
                    </Box>
                  </Box>
                </TileHeader>
                <TileBody display={isActive || hasError ? undefined : 'none'}>
                  {section.subsections.map(subsection => (
                    <React.Fragment key={subsection.title}>
                      {(subsection.matchError ||
                        subsection.showGeneralErrors) && (
                        <GeneralError match={subsection.matchError} />
                      )}

                      {subsection.rows.map((row: ?Row, rowIndex) =>
                        row ? (
                          <ToggleSection
                            title={row.toggle}
                            // eslint-disable-next-line react/no-array-index-key
                            key={rowIndex}
                            toggleQaId={row.toggleQaId}
                          >
                            <SectionFields row={row} fields={fields} />
                          </ToggleSection>
                        ) : (
                          <Divider
                            my={54}
                            // eslint-disable-next-line react/no-array-index-key
                            key={rowIndex}
                          />
                        )
                      )}
                    </React.Fragment>
                  ))}
                </TileBody>
              </Tile>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default MultiSection;
